import { SUMMONER_SPELLS } from '../../domain/summonerSpells.js';
import { listAllowedSummonerSpells } from '../../domain/map/summonerSpellMapRules.js';
import { getChampionLobbyDetails } from './championLobbyCatalog.js';
import {
  populateLobbyRuneSelect,
  bindLobbyRuneSelect
} from './lobbyRuneSelect.js';
import {
  populateLobbyMasterySelect,
  bindLobbyMasterySelect
} from './lobbyMasterySelect.js';

export class LobbyController {
  constructor(session, onMatchStartCallback, options = {}) {
    this.session = session;
    this.onMatchStartCallback = onMatchStartCallback;
    /** When set, lock-in sends loadout here instead of starting local sandbox match. */
    this.onMpLockIn = options.onMpLockIn || null;
    this.onLoadoutPersist = options.onLoadoutPersist || null;
    this.timerInterval = null;

    this.container = document.getElementById('lobby-overlay');
    this.timerBadge = document.getElementById('lobby-timer-text');
    this.championGrid = document.getElementById('champion-grid');
    this.spell1Select = document.getElementById('lobby-spell1-select');
    this.spell2Select = document.getElementById('lobby-spell2-select');
    this.mapSelect = document.getElementById('lobby-map-select');
    this.runeSelect = document.getElementById('lobby-rune-select');
    this.masterySelect = document.getElementById('lobby-mastery-select');
    this.lockInBtn = document.getElementById('lock-in-btn');

    this.previewAvatar = document.getElementById('preview-champion-avatar');
    this.previewName = document.getElementById('preview-champion-name');
    this.previewTitle = document.getElementById('preview-champion-title');
    this.previewAbilities = document.getElementById('preview-abilities-list');
  }

  init() {
    if (!this.container) return;

    this.populateSpells();
    this.populateMaps();
    populateLobbyRuneSelect(this.runeSelect, this.session.runePage);
    populateLobbyMasterySelect(this.masterySelect, this.session.masteryPage);
    this.renderChampionGrid();
    this.updatePreview();
    this.bindEvents();
    this.bindReopenLobbyButton();
    this.startTimer();
  }

  notifyLoadoutPersist() {
    if (typeof this.onLoadoutPersist === 'function') {
      this.onLoadoutPersist(this.session);
    }
  }

  bindReopenLobbyButton() {
    const btn = document.getElementById('reopen-lobby-btn');
    if (btn) btn.addEventListener('click', () => this.showLobby());
  }

  populateSpells() {
    const spellKeys = listAllowedSummonerSpells(this.session.selectedMapId);
    const createOptions = (selectedKey) => {
      const selected = spellKeys.includes(selectedKey) ? selectedKey : spellKeys[0];
      return spellKeys.map((key) => {
        const spell = SUMMONER_SPELLS[key];
        const isSelected = key === selected ? 'selected' : '';
        return `<option value="${key}" ${isSelected}>${spell.name} (${spell.cooldown}s CD)</option>`;
      }).join('');
    };

    if (this.spell1Select) {
      this.spell1Select.innerHTML = createOptions(this.session.spell1);
    }
    if (this.spell2Select) {
      this.spell2Select.innerHTML = createOptions(this.session.spell2);
    }
  }

  populateMaps() {
    if (!this.mapSelect) return;

    const MAP_LABELS = {
      aram_proving_grounds: '⚔️ The Proving Grounds (ARAM · MP test bed)',
      test_map: '🛠️ Test & Debug Sandbox',
      twisted_treeline_3v3: '🌲 Twisted Treeline (3v3)',
      summoners_rift_5v5: '🏰 Summoner\'s Rift (5v5)'
    };

    const options = this.session.availableMaps.map(mapId => {
      const label = MAP_LABELS[mapId] || mapId;
      const isSelected = mapId === this.session.selectedMapId ? 'selected' : '';
      return `<option value="${mapId}" ${isSelected}>${label}</option>`;
    }).join('');

    this.mapSelect.innerHTML = options;
  }

  renderChampionGrid() {
    if (!this.championGrid) return;

    this.championGrid.innerHTML = this.session.roster.map(champId => {
      const details = getChampionLobbyDetails(champId);
      const isSelected = champId === this.session.selectedChampionId ? 'selected' : '';
      return `
        <div class="champion-card ${isSelected}" data-champion="${champId}">
          <img src="${details.icon}" alt="${details.name}" onerror="this.onerror=null;this.src='data:image/svg+xml;utf8,<svg xmlns=&quot;http://www.w3.org/2000/svg&quot; width=&quot;72&quot; height=&quot;72&quot;><rect width=&quot;100%&quot; height=&quot;100%&quot; fill=&quot;%23334155&quot;/><text x=&quot;50%&quot; y=&quot;50%&quot; fill=&quot;%23f8fafc&quot; text-anchor=&quot;middle&quot; dominant-baseline=&quot;central&quot;>${details.name}</text></svg>';">
          <span class="champion-name">${details.name}</span>
          <span class="champion-role">${details.title}</span>
        </div>
      `;
    }).join('');

    this.championGrid.querySelectorAll('.champion-card').forEach(card => {
      card.addEventListener('click', () => {
        const champId = card.getAttribute('data-champion');
        if (this.session.selectChampion(champId)) {
          this.renderChampionGrid();
          this.updatePreview();
          this.notifyLoadoutPersist();
        }
      });
    });
  }

  updatePreview() {
    const champId = this.session.selectedChampionId;
    const details = getChampionLobbyDetails(champId);

    if (this.previewAvatar) {
      this.previewAvatar.src = details.icon || '';
    }
    if (this.previewName) {
      this.previewName.textContent = details.name;
    }
    if (this.previewTitle) {
      this.previewTitle.textContent = details.title;
    }
    if (this.previewAbilities) {
      this.previewAbilities.innerHTML = details.abilities.map(ab => `
        <div class="ability-item">
          <span class="ability-item-slot">[${ab.slot}]</span>
          <span class="ability-item-name">${ab.name}:</span> ${ab.desc}
        </div>
      `).join('');
    }
  }

  bindEvents() {
    if (this.spell1Select) {
      this.spell1Select.addEventListener('change', (e) => {
        const val1 = e.target.value;
        const val2 = this.spell2Select ? this.spell2Select.value : this.session.spell2;
        if (!this.session.setSummonerSpells(val1, val2)) {
          e.target.value = this.session.spell1;
          return;
        }
        this.notifyLoadoutPersist();
      });
    }

    if (this.spell2Select) {
      this.spell2Select.addEventListener('change', (e) => {
        const val2 = e.target.value;
        const val1 = this.spell1Select ? this.spell1Select.value : this.session.spell1;
        if (!this.session.setSummonerSpells(val1, val2)) {
          e.target.value = this.session.spell2;
          return;
        }
        this.notifyLoadoutPersist();
      });
    }

    if (this.mapSelect) {
      this.mapSelect.addEventListener('change', (e) => {
        const mapId = e.target.value;
        if (!this.session.selectMap(mapId)) {
          e.target.value = this.session.selectedMapId;
          return;
        }
        this.populateSpells();
        if (this.spell1Select) this.spell1Select.value = this.session.spell1;
        if (this.spell2Select) this.spell2Select.value = this.session.spell2;
        this.notifyLoadoutPersist();
      });
    }

    bindLobbyRuneSelect(this.runeSelect, this.session, () => this.notifyLoadoutPersist());
    bindLobbyMasterySelect(this.masterySelect, this.session, () => this.notifyLoadoutPersist());

    if (this.lockInBtn) {
      this.lockInBtn.addEventListener('click', () => {
        if (!this.session.lockIn()) return;
        this.notifyLoadoutPersist();
        if (typeof this.onMpLockIn === 'function') {
          this.finishMpLoadout();
          return;
        }
        this.finishLobby();
      });
    }
  }

  /** MP waiting-room loadout: hide overlay without starting local match. */
  finishMpLoadout() {
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.container) {
      this.container.hidden = true;
      this.container.style.display = 'none';
      this.container.style.zIndex = '';
    }
    this.onMpLockIn(this.session.getMatchConfig());
  }

  startTimer() {
    if (this.timerInterval) clearInterval(this.timerInterval);

    this.timerInterval = setInterval(() => {
      if (typeof this.onMpLockIn === 'function') {
        if (this.timerBadge) {
          this.timerBadge.textContent = 'MP';
        }
        return;
      }
      this.session.tickTimer();
      if (this.timerBadge) {
        this.timerBadge.textContent = `${this.session.timerSeconds}s`;
      }
      if (this.session.phase === 'MATCH_STARTING') {
        this.finishLobby();
      }
    }, 1000);
  }

  finishLobby() {
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.container) {
      this.container.hidden = true;
      this.container.style.display = 'none';
    }
    if (typeof this.onMatchStartCallback === 'function') {
      this.onMatchStartCallback(this.session.getMatchConfig());
    }
  }

  showLobby() {
    if (this.container) {
      this.container.hidden = false;
      this.container.style.display = 'flex';
      if (typeof this.onMpLockIn === 'function') {
        this.container.style.zIndex = '5200';
      }
    }
    this.session.isLockedIn = false;
    this.session.phase = 'SELECTING';
    this.session.timerSeconds = 60;
    this.startTimer();
  }
}
